import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const COUNTRY_DIAL = { ES: "+34", PT: "+351", BR: "+55" };

export function usePontoTab() {
  const { authAxios, user } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [activeClocks, setActiveClocks] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clockingIn, setClockingIn] = useState(false);
  const [clockingOut, setClockingOut] = useState(false);
  const [selectedMember, setSelectedMember] = useState("");
  const [geoStatus, setGeoStatus] = useState(null);
  const [location, setLocation] = useState(null);
  const [weeklyData, setWeeklyData] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteMember, setInviteMember] = useState("");
  const [inviteMethod, setInviteMethod] = useState("whatsapp");
  const [inviteContact, setInviteContact] = useState("");
  const [inviteResult, setInviteResult] = useState(null);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [countryDialCode, setCountryDialCode] = useState("+34");

  const fetchData = useCallback(async () => {
    try {
      const [aRes, mRes, wRes, rRes] = await Promise.all([
        authAxiosRef.current.get("/timeclock/active"),
        authAxiosRef.current.get("/deliverers/team/members"),
        authAxiosRef.current.get("/timeclock/weekly-summary"),
        authAxiosRef.current.get("/restaurant"),
      ]);
      setActiveClocks(aRes.data);
      setMembers(mRes.data);
      setWeeklyData(wRes.data);
      const country = (rRes.data?.country || "ES").toUpperCase();
      setCountryDialCode(COUNTRY_DIAL[country] || "+34");
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getLocation = () => {
    return new Promise((resolve) => {
      setGeoStatus("loading");
      if (!navigator.geolocation) {
        setGeoStatus("error");
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setLocation(loc);
          setGeoStatus("success");
          resolve(loc);
        },
        () => {
          setGeoStatus("error");
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  };

  const handleClockIn = async () => {
    const memberId = selectedMember || user?.user_id;
    if (!memberId) { toast.error("Selecione um funcionário"); return; }
    setClockingIn(true);
    const loc = await getLocation();
    try {
      const member = members.find((m) => m.id === selectedMember);
      await authAxios.post("/timeclock/clock-in", {
        member_id: memberId,
        member_name: member?.name || user?.name || "",
        lat: loc?.lat,
        lng: loc?.lng,
      });
      toast.success("Entrada registrada!");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao registrar entrada");
    }
    setClockingIn(false);
  };

  const handleClockOut = async (memberId) => {
    setClockingOut(true);
    const loc = await getLocation();
    try {
      const res = await authAxios.post("/timeclock/clock-out", {
        member_id: memberId,
        lat: loc?.lat,
        lng: loc?.lng,
      });
      toast.success(`Saída registrada! ${res.data.hours_worked}h trabalhadas`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao registrar saída");
    }
    setClockingOut(false);
  };

  const handleSendInvite = async () => {
    if (!inviteMember || !inviteContact) { toast.error("Preencha todos os campos"); return; }
    setSendingInvite(true);
    try {
      const payload = {
        method: inviteMethod,
        base_url: window.location.origin,
        ...(inviteMethod === "whatsapp" ? { phone: inviteContact } : { email: inviteContact }),
      };
      const res = await authAxios.post(`/timeclock/send-invite/${inviteMember}`, payload);
      setInviteResult(res.data);
      if (inviteMethod === "whatsapp") {
        if (res.data.sent) {
          toast.success("WhatsApp enviado!");
        } else {
          toast.info("Link gerado! Envie manualmente pelo botão abaixo.");
        }
      } else {
        if (res.data.sent) {
          toast.success("Email enviado!");
        } else {
          toast.warning("Link gerado, mas email não foi enviado. Copie o link manualmente.");
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar");
    }
    setSendingInvite(false);
  };

  const getElapsedTime = (clockIn) => {
    const diff = (new Date() - new Date(clockIn)) / 1000;
    const h = Math.floor(diff / 3600);
    const m = Math.floor((diff % 3600) / 60);
    return `${h}h ${m}m`;
  };

  const openInviteDialog = () => {
    setShowInvite(true);
    setInviteResult(null);
    setInviteContact(countryDialCode);
  };

  const closeInviteDialog = () => {
    setShowInvite(false);
    setInviteResult(null);
  };

  const resetInviteForm = () => {
    setInviteResult(null);
    setInviteContact(inviteMethod === "whatsapp" ? countryDialCode : "");
  };

  const handleInviteMethodChange = (method) => {
    setInviteMethod(method);
    setInviteContact(method === "whatsapp" ? countryDialCode : "");
    setInviteResult(null);
  };

  return {
    activeClocks, members, loading,
    clockingIn, clockingOut,
    selectedMember, setSelectedMember,
    geoStatus, location,
    weeklyData,
    showInvite, setShowInvite, openInviteDialog, closeInviteDialog,
    inviteMember, setInviteMember,
    inviteMethod, setInviteMethod: handleInviteMethodChange,
    inviteContact, setInviteContact,
    inviteResult,
    sendingInvite,
    handleClockIn, handleClockOut,
    handleSendInvite,
    getElapsedTime,
    resetInviteForm,
    countryDialCode,
  };
}
